import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as Highcharts from 'highcharts/highmaps';
import { WorldMapService } from '@shared/services/world-map.service';
import _ from 'lodash';
import { CountryService } from '@shared/services/country.service';
import { AuthService } from '@shared/services/auth.service';
import { CountryData } from '../../../../theme/dashboard/dashboard.component';
import { NgIf } from '@angular/common';
import { HighchartsChartModule } from 'highcharts-angular';
import { HighChartsI18n } from '@shared/components/highcharts/i18n';

export interface CountryClickEvent {
  countryCode: string;
}

@Component({
  selector: 'app-world-map',
  templateUrl: './world-map.component.html',
  styleUrls: ['./world-map.component.scss'],
  imports: [NgIf, HighchartsChartModule],
})
export class WorldMapComponent implements OnInit, OnChanges {
  @Input({ required: true }) data: CountryData[];
  @Input() titleKey: string | null;
  @Input() seriesNameKey: string | null;
  @Input() showTopCountryNames = true;
  @Input() height: string = null;
  @Input() colorStops: [number, string][];

  @Output() countryClick = new EventEmitter<CountryClickEvent>();

  @Input() nbTopCountries = 5;

  Highcharts: typeof Highcharts = Highcharts;
  options: Highcharts.Options;

  constructor(
    private translate: TranslateService,
    private readonly i18n: HighChartsI18n,
    private worldMap: WorldMapService,
    private countryService: CountryService,
    private auth: AuthService,
  ) {}

  async ngOnInit() {
    await this.worldMap.init();
    if (!this.options) {
      this.buildOptions();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.data) {
      this.buildOptions();
    }
  }

  buildOptions() {
    if (!this.worldMap.getMap()) {
      return;
    }
    if (!this.data) {
      this.options = null;
      return;
    }

    const worldMap = this.worldMap.getMap();
    const countryNames = this.translateCountryNames(worldMap);

    const topCountries = _(this.data)
      .sortBy(([country, value]) => -value)
      .take(this.nbTopCountries)
      .map(([country, value], index) => ({
        country,
        value,
        label: `${index + 1}. ${countryNames[country] ?? country}`,
      }))
      .keyBy((data) => data.country)
      .value();

    this.options = {
      accessibility: {
        enabled: false,
      },
      chart: {
        map: worldMap,
        borderRadius: 5,
      },
      credits: {
        enabled: false,
      },
      title: {
        text: this.titleKey ? this.translate.instant(this.titleKey) : null,
        style: { display: 'block' },
      },
      legend: {
        align: 'right',
        padding: 20,
        y: 30,
      },
      colorAxis: {
        min: 0,
        stops: this.colorStops,
      },
      mapNavigation: {
        enabled: true,
        enableMouseWheelZoom: false,
        buttonOptions: {
          verticalAlign: 'bottom',
        },
      },
      plotOptions: {
        map: {
          states: {
            hover: {
              borderWidth: 1,
              borderColor: '#c3c1c1',
            },
          },
        },
        series: {
          point: {
            events: {
              mouseOver: function () {
                if (this.value !== null && typeof this.value !== 'undefined') {
                  this.series.chart.container.style.cursor = 'pointer';
                } else {
                  this.series.chart.container.style.cursor = 'default';
                }
              },
              mouseOut: function () {
                this.series.chart.container.style.cursor = 'default';
              },
              click: (event) => this.countryClick.emit({ countryCode: event.point['iso-a2'] }),
            },
          },
        },
      },
      series: [
        {
          type: 'map',
          data: this.data,
          joinBy: 'iso-a2',
          keys: ['iso-a2', 'value'],
          name: this.seriesNameKey ? this.translate.instant(this.seriesNameKey) : null,
          dataLabels: {
            allowOverlap: true,
            enabled: this.showTopCountryNames && this.data?.length > 0,
            formatter: function () {
              return topCountries[this['iso-a2']]?.label;
            },
            nullFormat: '',
          },
        },
      ],
      exporting: {
        chartOptions: {
          chart: {
            backgroundColor: '#FFFFFF',
          },
        },
        buttons: {
          contextButton: {
            y: -15,
            menuItems: [
              'printChart',
              'separator',
              'downloadPNG',
              'downloadJPEG',
              'downloadSVG',
              'separator',
              'downloadCSV',
              'downloadXLS',
            ],
          },
        },
      },
      lang: this.i18n.langOptions,
    };

    if (this.height) this.options.chart.height = this.height;
  }

  private translateCountryNames(worldMap) {
    const countryNames = this.countryService.getCountryNames();
    const lang = this.auth.getCurrentLanguage();

    worldMap.features.forEach((c) => {
      if (c.properties.name) {
        const countryName = countryNames[c.properties['iso-a2']];
        if (countryName) c.properties.name = countryNames[c.properties['iso-a2']];
        else console.warn(`No translation for country: ${lang} ${c.properties['iso-a2']} ${c.properties.name}`);
      }
    });
    return countryNames;
  }
}
